import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Video, Award, Languages } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FOREST_IMG =
  "https://images.unsplash.com/photo-1547014762-3a94fb4df70a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxjYWxpZm9ybmlhJTIwY29hc3QlMjBmb2clMjByZWR3b29kJTIwZm9yZXN0JTIwc2VyZW5lJTIwbW9ybmluZ3xlbnwwfHx8fDE3ODg4MTI0MzN8MA&ixlib=rb-4.1.0&q=85";

const services = [
  {
    title: "Terapia de Pareja",
    copy: "Para parejas que se aman pero no logran encontrarse: conflicto constante, distancia emocional, infidelidad, crianza o decisiones difíciles sobre el futuro.",
    to: "/couples-therapy",
  },
  {
    title: "Infidelidad y Confianza",
    copy: "Un espacio estructurado y sin juicios para entender lo que pasó, atender el impacto de la traición y decidir con claridad lo que sigue.",
    to: "/infidelity-trust",
  },
  {
    title: "Terapia Individual y Familiar",
    copy: "Apoyo para ansiedad, depresión, trauma, transiciones de vida y dinámicas familiares — en un ambiente cálido y práctico.",
    to: "/individual-therapy",
  },
];

const fees = [
  { s: "Terapia Individual", d: "Virtual · 50 minutos", p: "$220" },
  { s: "Terapia de Pareja", d: "50 minutos", p: "$250+" },
  { s: "Sesión Inicial de Pareja", d: "90 minutos · recomendada", p: "$375" },
  { s: "Terapia Familiar", d: "50 minutos", p: "$275" },
  { s: "Sesión Inicial Familiar", d: "90 minutos · recomendada", p: "$375" },
];

const faqs = [
  {
    q: "¿Toda la terapia puede ser en español?",
    a: "Sí. Ofrezco terapia individual, de pareja y familiar completamente en español. En las sesiones de pareja, cada persona puede expresarse en el idioma que le resulte más natural — podemos movernos entre inglés y español sin problema.",
  },
  {
    q: "¿Cómo funcionan las sesiones en línea?",
    a: "Las sesiones se realizan por video a través de una plataforma segura. Puede conectarse desde cualquier lugar de California — su casa, su oficina, incluso desde lugares distintos si son una pareja. Muchas parejas encuentran que la comodidad del telehealth hace más fácil ser constantes.",
  },
  {
    q: "¿Trabaja con seguros médicos?",
    a: "Trabajo con Lyra Health y Spring Health. También acepto tarjetas HSA/FSA y proporciono superbills para clientes de pago privado que deseen solicitar reembolso a su seguro.",
  },
  {
    q: "¿Qué pasa después de enviar la solicitud de consulta?",
    a: "Reviso personalmente cada solicitud y respondo en uno o dos días hábiles. Puede escribirme en español — con gusto le respondo en español. Si parecemos una buena opción, agendamos una consulta gratuita de 20 minutos.",
  },
];

const Espanol = () => {
  usePageMeta(
    "Terapia de Pareja en Español — California | Holistic Healing Psychotherapy, Inc.",
    "Terapia de pareja, individual y familiar en español en toda California. Terapeuta bilingüe (LCSW) con casi 20 años de experiencia clínica. Telehealth seguro. Consulta gratuita de 20 minutos."
  );

  return (
    <div data-testid="espanol-page">
      <section className="bg-cream-50 pt-14 pb-16 lg:pt-24 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-6" data-testid="es-eyebrow">Terapia de Pareja · California · Telehealth Seguro</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-ink" data-testid="es-headline">
                Terapia de pareja para relaciones que{" "}
                <em className="text-mauve-500">valen la pena</em>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-sage leading-relaxed max-w-xl" data-testid="es-subcopy">
                Terapia en español, con experiencia y calidez, para parejas que enfrentan desconexión, conflicto,
                infidelidad, problemas de comunicación y grandes transiciones de la vida.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="es-schedule-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm tracking-wide px-7 py-4 transition-all duration-300 hover:bg-forest-700 hover:shadow-[0_12px_32px_rgba(18,55,42,0.3)]"
                >
                  Agende una Consulta Gratuita <ArrowRight size={16} />
                </Link>
                <a
                  href="tel:+16573339466"
                  data-testid="es-phone-cta"
                  className="inline-flex items-center rounded-full border border-forest-800/25 text-forest-800 font-semibold text-sm tracking-wide px-7 py-4 transition-colors duration-300 hover:border-gold-500 hover:text-gold-600"
                >
                  (657) 333-9466
                </a>
              </div>
              <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.2em] text-sage" data-testid="es-trust-line">
                <span className="flex items-center gap-2"><Award size={14} className="text-gold-500" /> LCSW de California</span>
                <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-gold-500" /> Casi 20 Años de Experiencia Clínica</span>
                <span className="flex items-center gap-2"><Video size={14} className="text-gold-500" /> Telehealth Seguro</span>
                <span className="flex items-center gap-2"><Languages size={14} className="text-gold-500" /> Bilingüe: Inglés y Español</span>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.15} className="lg:col-span-5 editorial-frame">
            <img
              src={FOREST_IMG}
              alt="Niebla tranquila sobre un bosque de California"
              className="relative z-10 w-full h-[340px] sm:h-[460px] object-cover"
              data-testid="es-hero-image"
            />
          </FadeIn>
        </div>
      </section>

      <section className="bg-forest-900 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading dark eyebrow="Cómo Puedo Ayudar" title="Un espacio seguro, sin juicios, y con dirección." />
          <FadeIn delay={0.1}>
            <p className="mt-4 text-base sm:text-lg text-cream-50/70 leading-relaxed max-w-2xl" data-testid="es-intro">
              Puede que se amen y aun así se encuentren atrapados en las mismas discusiones, cargando resentimiento o
              preguntándose si la relación se puede reparar. La terapia ofrece un lugar estructurado y de apoyo para
              entender lo que está pasando — y empezar a hacer algo diferente.
            </p>
          </FadeIn>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-cream-50/10 bg-forest-800/50 p-8" data-testid={`es-service-${i + 1}`}>
                  <p className="eyebrow mb-4">{`0${i + 1}`}</p>
                  <h3 className="font-serif text-2xl text-cream-50 mb-3">{s.title}</h3>
                  <p className="text-sm text-cream-50/70 leading-relaxed">{s.copy}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Inversión" title="Precios claros. Sin sorpresas." />
          <FadeIn delay={0.1}>
            <div className="mt-10" data-testid="es-fees-table">
              {fees.map((f, i) => (
                <div key={f.s} className="flex items-baseline justify-between gap-6 py-5 border-t border-forest-800/10" data-testid={`es-fee-row-${i + 1}`}>
                  <div>
                    <p className="font-serif text-xl sm:text-2xl text-forest-800">{f.s}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-sage mt-1">{f.d}</p>
                  </div>
                  <p className="font-serif text-2xl sm:text-3xl text-gold-600 shrink-0">{f.p}</p>
                </div>
              ))}
              <p className="pt-6 text-sm text-sage leading-relaxed" data-testid="es-fees-note">
                Citas presenciales disponibles solo los sábados (Long Beach); las tarifas presenciales pueden variar.
                Acepto tarjetas HSA/FSA y proporciono superbills para clientes de pago privado.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Preguntas Frecuentes" title="Antes de comunicarse." />
          <FadeIn delay={0.15}>
            <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="es-faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`es-faq-${i}`}
                  className="rounded-xl border border-forest-800/10 bg-white/60 px-6 data-[state=open]:border-gold-500/50 transition-colors duration-300"
                >
                  <AccordionTrigger className="text-left font-serif text-lg sm:text-xl text-forest-800 hover:text-gold-600 hover:no-underline py-5" data-testid={`es-faq-question-${i + 1}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-sage leading-relaxed pb-6" data-testid={`es-faq-answer-${i + 1}`}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="mt-12 rounded-2xl border border-gold-500/35 bg-cream-100 p-8" data-testid="es-contact-card">
              <p className="font-serif text-2xl text-forest-800 mb-2">Hablemos de lo que está pasando</p>
              <p className="text-sm text-sage leading-relaxed mb-6">
                Una consulta gratuita de 20 minutos nos da la oportunidad de hablar sobre lo que le trae, lo que espera
                cambiar, y si soy la terapeuta adecuada para usted. Puede escribirme en español.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="es-bottom-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-3.5 transition-colors duration-300 hover:bg-forest-700"
                >
                  Agende una Consulta <ArrowRight size={15} />
                </Link>
                <a href="mailto:bqholistichealing@gmail.com" data-testid="es-email-link" className="inline-flex items-center rounded-full border border-forest-800/25 text-forest-800 font-semibold text-sm px-7 py-3.5 transition-colors duration-300 hover:border-gold-500 hover:text-gold-600">
                  bqholistichealing@gmail.com
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="No tiene que resolver esto solo(a)."
        copy="Agende una consulta gratuita de 20 minutos para ver si somos una buena opción."
      />
    </div>
  );
};

export default Espanol;
